import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ORGANIZATION_QUERY_KEYS } from "@/shared/hooks/useOrganizationQuery";
import {
  buildPaymentOffer, findOpenCardRequest, findOpenTransfer, hasStripeMandate, resolveOfferState, summarizeUnpaidRejections,
} from "../../utils/unpaidPayment.utils";
import { useDirectDebitQuery } from "../useDirectDebitQuery";
import type {
  PaymentSnapshot, UnpaidPayment, UnpaidPaymentAction, UnpaidPaymentContext,
} from "../../types/unpaidInvoices.types";

type UseUnpaidPaymentParams = {
  isEnabled: boolean
  organizationId: string | undefined
  /** Where `open` starts the flow. */
  context: UnpaidPaymentContext
  noticeAmountCents: number
};

const useSituationOffer = (isEnabled: boolean) => {
  const situationQuery = useDirectDebitQuery({ enabled: isEnabled });

  return {
    situation: situationQuery.data,
    offerState: resolveOfferState({
      hasData: situationQuery.data !== undefined,
      hasFailed: situationQuery.errorUpdateCount > 0,
      isFetching: situationQuery.isFetching,
    }),
    retryOffer: () => {
      void situationQuery.refetch();
    },
  };
};

export const useUnpaidPayment = ({
  isEnabled, organizationId, context, noticeAmountCents,
}: UseUnpaidPaymentParams): UnpaidPayment => {
  const queryClient = useQueryClient();
  const { situation, offerState, retryOffer } = useSituationOffer(isEnabled);
  const [snapshot, setSnapshot] = useState<PaymentSnapshot | null>(null);
  const [previousOrganizationId, setPreviousOrganizationId] = useState(organizationId);

  // The gate sits above the router and outlives the session: a logout resets Redux, not this state.
  // Dropping the snapshot while rendering (not in an effect) keeps a stale one from flashing for a frame
  // and from resurfacing when the same organization signs back in.
  if (previousOrganizationId !== organizationId) {
    setPreviousOrganizationId(organizationId);
    setSnapshot(null);
  }

  const {
    payableRejectionIds, payableInstallmentIds, payableCents, pendingCents,
  } = summarizeUnpaidRejections(situation?.rejections, situation?.overdue_installments);

  // The dialog keeps the rejections it opened with: once the request is created the situation
  // flips them to "being paid", which would otherwise empty the dialog under the user's eyes.
  const offer = buildPaymentOffer(payableRejectionIds, payableCents, findOpenCardRequest(situation), payableInstallmentIds);
  const payAction: UnpaidPaymentAction | undefined = offer && {
    payableCents: offer.totalCents,
    open: () => setSnapshot({ ...offer, context, noticeAmountCents }),
  };

  const closePayment = () => {
    setSnapshot(null);
    if (organizationId) {
      void queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEYS.detail(organizationId) });
    }
  };

  return {
    payAction,
    pendingCents,
    canDebit: hasStripeMandate(situation?.accounts ?? []),
    publishableKey: situation?.stripe_publishable_key ?? null,
    snapshot,
    closePayment,
    offerState,
    retryOffer,
    openTransfer: findOpenTransfer(situation),
    accounts: situation?.accounts ?? [],
    codeChannels: situation?.code_channels ?? {},
  };
};
